import { type ForwardedRef, type MouseEvent, type ReactNode, forwardRef } from 'react';

import { Icons } from '..';
import clsx from 'clsx';

import { Close } from '../Icons';

import styles from './Chip.module.scss';

export type ChipProps = {
  label: ReactNode;
  onRemove?: () => void;
  onClick?: (e?: MouseEvent<HTMLDivElement>) => void;
  className?: string;
  disabled?: boolean;
  isDropdown?: boolean;
};

export const Chip = forwardRef<HTMLDivElement, ChipProps>(
  (
    { label, onRemove, onClick, className, disabled = false, isDropdown = false },
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const handleRemoveClick = (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      onRemove?.();
    };

    const handleChipClick = (e: MouseEvent<HTMLDivElement>) => {
      if (!disabled && onClick) {
        onClick(e);
      }
    };

    return (
      <div
        ref={ref}
        className={clsx(
          styles.chip,
          {
            [styles.clickable]: !!onClick && !disabled,
            [styles.disabled]: disabled,
          },
          className,
        )}
        onClick={handleChipClick}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick && !disabled ? 0 : undefined}
        onKeyDown={
          onClick && !disabled
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick(e as unknown as MouseEvent<HTMLDivElement>);
                }
              }
            : undefined
        }
      >
        <span className={styles.label}>{label}</span>
        {isDropdown && <Icons.ChevronDown size={16} />}
        {onRemove && (
          <button
            type="button"
            className={styles['remove-button']}
            onClick={handleRemoveClick}
            aria-label="Remove"
            disabled={disabled}
          >
            <Close size={14} />
          </button>
        )}
      </div>
    );
  },
);

Chip.displayName = 'Chip';
